import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as echarts from "echarts";
import "echarts-gl";
import ReactEcharts from "echarts-for-react";
import { Button } from "react-bootstrap";
import GYAxios from "../../../util/GYAxios";
import LoaderGraph from "../../general/LoaderGraph";

const NORMAL_COLOR = "#5b9bd5";
const PROBLEM_COLOR = "#e05c5c";

export default function IssueFinder() {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [analysisData, setAnalysisData] = useState(null);
  const [regressionLine, setRegressionLine] = useState(null);
  const [surface3d, setSurface3d] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch site list on mount
  useEffect(() => {
    GYAxios.get("/issue/issue-finder/sites")
      .then((res) => {
        const options = res.data.map((site) => ({
          label: `${site.company} ${site.site_name}`,
          value: site.table_name,
          site_id: site.site_id,
        }));
        setSites(options);
      })
      .catch((err) => {
        console.error("Failed to fetch sites:", err);
      });
  }, []);

  const handleAnalyze = () => {
    if (!selectedSite || !selectedMonth) return;

    const year = selectedMonth.getFullYear().toString();
    const mm = String(selectedMonth.getMonth() + 1).padStart(2, "0");
    const month = `${year}${mm}`;

    setLoading(true);
    setError(null);
    setAnalysisData(null);
    setRegressionLine(null);
    setSurface3d(null);

    GYAxios.get(
      `/issue/issue-finder/analyze?table_name=${encodeURIComponent(
        selectedSite.value
      )}&month=${month}`,
      { timeout: 120000 }
    )
      .then((res) => {
        setAnalysisData(res.data.data);
        setRegressionLine(res.data.regression);
        setSurface3d(res.data.surface_3d || null);
      })
      .catch((err) => {
        console.error("Analysis failed:", err);
        setError("Analysis failed. Please check the console and try again.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Dates flagged as problematic by the 3D model (any inverter-hour below prediction − 1σ)
  const prob3dDates = useMemo(() => {
    if (!surface3d) return new Set();
    return new Set(
      surface3d.scatter_problematic.map((pt) => String(pt[3]).slice(0, 10))
    );
  }, [surface3d]);

  // Build ECharts option
  const chartOption = useMemo(() => {
    if (!analysisData || analysisData.length === 0) return null;

    const dates = analysisData.map((d) => d.dateData);
    const seriesData = analysisData.map((d) => {
      const isProb = d.is_problematic || prob3dDates.has(String(d.dateData).slice(0, 10));
      return {
        value: d.brut_PR,
        itemStyle: { color: isProb ? PROBLEM_COLOR : NORMAL_COLOR },
      };
    });

    return {
      title: {
        text: `Daily brut_PR — ${selectedSite ? selectedSite.label : ""}`,
        left: "center",
        textStyle: { fontSize: 14 },
      },
      tooltip: {
        trigger: "axis",
        formatter: (params) => {
          const p = params[0];
          if (!p) return "";
          const day = analysisData[p.dataIndex];
          const prStr =
            p.value !== null && p.value !== undefined
              ? Number(p.value).toFixed(2)
              : "N/A";
          const isProb = day && (day.is_problematic || prob3dDates.has(String(day.dateData).slice(0, 10)));
          const warn = isProb ? "<br/><b>⚠ Problematic day</b>" : "";
          return `${p.name}<br/>brut_PR: ${prStr}${warn}`;
        },
      },
      grid: { bottom: 90 },
      xAxis: {
        type: "category",
        data: dates,
        axisLabel: { rotate: 45, fontSize: 10 },
      },
      yAxis: {
        type: "value",
        name: "brut_PR",
        nameLocation: "end",
      },
      series: [
        {
          type: "bar",
          data: seriesData,
        },
      ],
    };
  }, [analysisData, selectedSite, prob3dDates]);

  const problematicDays = useMemo(
    () => (analysisData || []).filter(
      (d) => d.is_problematic || prob3dDates.has(String(d.dateData).slice(0, 10))
    ),
    [analysisData, prob3dDates]
  );

  // Build ECharts scatter option
  const scatterOption = useMemo(() => {
    if (!analysisData || analysisData.length === 0) return null;

    const normalPoints = [];
    const problemPoints = [];
    const allTemps = [];
    const allPRs = [];

    (analysisData || []).forEach((d) => {
      if (d.module_temp == null || d.brut_PR == null) return;
      const point = { value: [d.module_temp, d.brut_PR], name: d.dateData };
      const isProb = d.is_problematic || prob3dDates.has(String(d.dateData).slice(0, 10));
      if (isProb) problemPoints.push(point);
      else normalPoints.push(point);
      allTemps.push(d.module_temp);
      allPRs.push(d.brut_PR);
    });

    const xMin = allTemps.length ? Math.max(Math.min(...allTemps), 0) : 0;
    const xMax = allTemps.length ? Math.min(Math.max(...allTemps), 100) : 100;
    const yMin = allPRs.length ? Math.max(Math.min(...allPRs), 0) : 0;
    const yMax = allPRs.length ? Math.min(Math.max(...allPRs), 100) : 100;

    const series = [
      {
        name: "Normal",
        type: "scatter",
        data: normalPoints,
        itemStyle: { color: NORMAL_COLOR, opacity: 0.85 },
        symbolSize: 9,
      },
      {
        name: "Problematic",
        type: "scatter",
        data: problemPoints,
        itemStyle: { color: PROBLEM_COLOR, opacity: 0.9 },
        symbolSize: 11,
        symbol: "diamond",
      },
    ];

    if (regressionLine) {
      const { slope, intercept, temp_min, temp_max } = regressionLine;
      series.push({
        name: "Regression",
        type: "line",
        data: [
          [temp_min, slope * temp_min + intercept],
          [temp_max, slope * temp_max + intercept],
        ],
        lineStyle: { color: "#aaa", type: "dashed", width: 1.5 },
        itemStyle: { color: "#aaa" },
        symbol: "none",
        tooltip: { show: false },
      });
    }

    return {
      title: {
        text: "Module Temperature vs brut_PR",
        left: "center",
        textStyle: { fontSize: 14 },
      },
      legend: {
        bottom: 0,
        data: ["Normal", "Problematic"],
      },
      tooltip: {
        trigger: "item",
        formatter: (params) => {
          if (!params.name) return "";
          return `${params.name}<br/>Temp: ${Number(params.value[0]).toFixed(1)}<br/>brut_PR: ${Number(params.value[1]).toFixed(2)}${params.seriesName === "Problematic" ? "<br/><b>⚠ Problematic day</b>" : ""}`;
        },
      },
      grid: { bottom: 60 },
      xAxis: {
        type: "value",
        name: "Module Temp (avg °C, 10-min intervals)",
        nameLocation: "middle",
        nameGap: 30,
        min: xMin,
        max: xMax,
      },
      yAxis: {
        type: "value",
        name: "brut_PR",
        nameLocation: "end",
        min: yMin,
        max: yMax,
      },
      series,
    };
  }, [analysisData, regressionLine, prob3dDates]);

  // Build ECharts GL 3D scatter + surface chart
  const chart3DOption = useMemo(() => {
    if (!surface3d) return null;

    const series = [
      {
        type: "scatter3D",
        name: "Normal",
        data: surface3d.scatter_normal,
        symbolSize: 4,
        itemStyle: { color: NORMAL_COLOR, opacity: 0.75 },
        tooltip: {
          formatter: (p) =>
            `<b>INV ${p.value[4]}</b> — ${p.value[3]}<br/>Temp: ${p.value[0]}°C<br/>Inc: ${p.value[1]}°<br/>PR: ${p.value[2]}%`,
        },
      },
      {
        type: "scatter3D",
        name: "Problematic",
        data: surface3d.scatter_problematic,
        symbolSize: 6,
        itemStyle: { color: PROBLEM_COLOR, opacity: 0.95 },
        tooltip: {
          formatter: (p) =>
            `<b>INV ${p.value[4]}</b> — ${p.value[3]}<br/>Temp: ${p.value[0]}°C<br/>Inc: ${p.value[1]}°<br/>PR: ${p.value[2]}%<br/><b>⚠ Below prediction − 2σ</b>`,
        },
      },
      {
        type: "surface",
        name: "Regression",
        data: surface3d.surface_mean,
        rows: 30,
        columns: 30,
        wireframe: { show: false },
        itemStyle: { color: "rgba(74,144,217,0.55)" },
        tooltip: { show: false },
      },
      {
        type: "surface",
        name: "+2σ",
        data: surface3d.surface_upper,
        rows: 30,
        columns: 30,
        wireframe: { show: false },
        itemStyle: { color: "rgba(0,160,0,0.25)" },
        tooltip: { show: false },
      },
      {
        type: "surface",
        name: "-2σ",
        data: surface3d.surface_lower,
        rows: 30,
        columns: 30,
        wireframe: { show: false },
        itemStyle: { color: "rgba(220,50,50,0.3)" },
        tooltip: { show: false },
      },
    ];

    return {
      title: {
        text: "PR vs Module Temperature vs Incidence Angle",
        subtext: `Polynomial Regression (degree 2)  |  R² = ${surface3d.r2}  |  σ = ${surface3d.std}`,
        left: "center",
        textStyle: { fontSize: 14 },
        subtextStyle: { fontSize: 12, color: "#888" },
      },
      tooltip: { trigger: "item" },
      legend: {
        bottom: 4,
        data: ["Normal", "Problematic"],
        textStyle: { fontSize: 12 },
      },
      grid3D: {
        viewControl: { projection: "perspective", distance: 220, alpha: 20, beta: 40 },
        boxWidth: 100,
        boxHeight: 80,
        boxDepth: 100,
        light: { main: { intensity: 1.2, shadow: false } },
        postEffect: { enable: false },
      },
      xAxis3D: {
        type: "value",
        name: "Module Temp (°C)",
        nameTextStyle: { fontSize: 11 },
      },
      yAxis3D: {
        type: "value",
        name: "Incidence Angle (°)",
        nameTextStyle: { fontSize: 11 },
      },
      zAxis3D: {
        type: "value",
        name: "PR (%)",
        min: 0,
        max: 100,
        nameTextStyle: { fontSize: 11 },
      },
      series,
    };
  }, [surface3d]);

  return (
    <div style={{ padding: "20px" }}>
      <h4>New Issue Finder</h4>
      <p style={{ color: "#666", fontSize: "13px", marginBottom: "20px" }}>
        Automatically detect days with abnormally low Performance Ratio (PR)
        using statistical regression analysis.
      </p>

      {/* Controls */}
      <div
        style={{
          display: "flex",
          gap: "16px",
          alignItems: "flex-end",
          flexWrap: "wrap",
          marginBottom: "24px",
        }}
      >
        <div style={{ width: "450px" }}>
          <label
            style={{
              fontWeight: 600,
              fontSize: "13px",
              display: "block",
              marginBottom: "4px",
            }}
          >
            Site
          </label>
          <Select
            options={sites}
            value={selectedSite}
            onChange={setSelectedSite}
            placeholder="Select a site..."
            styles={{
              container: (provided) => ({
                ...provided,
                width: '300px', // Sets the width of the main input box
              }),
              control: (provided) => ({
                ...provided,
                width: '300px', // Sets the width of the main input box
              }),
              menu: (provided) => ({
                ...provided,
                width: 'max-content',
                minWidth: '100%', // Ensures it is never narrower than the input box
              }),
            }}
            isClearable
          />
        </div>

        <div>
          <label
            style={{
              fontWeight: 600,
              fontSize: "13px",
              display: "block",
              marginBottom: "4px",
            }}
          >
            Month
          </label>
          <DatePicker
            selected={selectedMonth}
            onChange={(date) => setSelectedMonth(date)}
            dateFormat="MM/yyyy"
            showMonthYearPicker
            className="form-control"
            placeholderText="Select month"
          />
        </div>

        <Button
          bsStyle="primary"
          onClick={handleAnalyze}
          disabled={!selectedSite || !selectedMonth || loading}
        >
          {loading ? "Analyzing..." : "Analyze"}
        </Button>
      </div>

      {/* Error */}
      {error && <div className="alert alert-danger">{error}</div>}

      {/* Loading */}
      {loading && <LoaderGraph style={{ height: "400px" }} />}

      {/* Results */}
      {!loading && analysisData && (
        <>
          {chartOption && (
            <ReactEcharts
              option={chartOption}
              style={{ height: "400px", width: "100%" }}
              notMerge={true}
            />
          )}

          {scatterOption && (
            <ReactEcharts
              option={scatterOption}
              style={{ height: "380px", width: "100%", marginTop: "24px" }}
              notMerge={true}
            />
          )}

          {/* Legend */}
          <div
            style={{
              display: "flex",
              gap: "20px",
              marginTop: "10px",
              marginBottom: "12px",
            }}
          >
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "13px",
              }}
            >
              <span
                style={{
                  width: 14,
                  height: 14,
                  background: NORMAL_COLOR,
                  display: "inline-block",
                  borderRadius: 2,
                }}
              />
              Normal day
            </span>
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "13px",
              }}
            >
              <span
                style={{
                  width: 14,
                  height: 14,
                  background: PROBLEM_COLOR,
                  display: "inline-block",
                  borderRadius: 2,
                }}
              />
              Problematic day (low PR)
            </span>
          </div>

          {/* Summary */}
          {problematicDays.length > 0 ? (
            <div className="alert alert-warning">
              <strong>
                {problematicDays.length} problematic day
                {problematicDays.length > 1 ? "s" : ""} detected:
              </strong>{" "}
              {problematicDays.map((d) => d.dateData).join(", ")}
            </div>
          ) : (
            <div className="alert alert-success">
              No problematic days detected for this period.
            </div>
          )}
        </>
      )}

      {/* Empty state */}
      {!loading && !analysisData && !error && (
        <p style={{ color: "#999", fontSize: "13px" }}>
          Select a site and month, then click Analyze to detect problematic PR
          days.
        </p>
      )}

      {/* 3D PR Surface Chart */}
      {!loading && chart3DOption && (
        <div style={{ marginTop: "32px" }}>
          <ReactEcharts
            echarts={echarts}
            option={chart3DOption}
            style={{ height: "600px", width: "100%" }}
            notMerge={true}
          />
          <div
            style={{
              display: "flex",
              gap: "20px",
              marginTop: "8px",
              flexWrap: "wrap",
              fontSize: "12px",
              color: "#555",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span
                style={{
                  width: 14,
                  height: 14,
                  background: "rgba(74,144,217,0.55)",
                  display: "inline-block",
                  borderRadius: 2,
                }}
              />
              Regression surface
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span
                style={{
                  width: 14,
                  height: 14,
                  background: "rgba(0,160,0,0.4)",
                  display: "inline-block",
                  borderRadius: 2,
                }}
              />
              +2σ upper bound
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span
                style={{
                  width: 14,
                  height: 14,
                  background: "rgba(220,50,50,0.45)",
                  display: "inline-block",
                  borderRadius: 2,
                }}
              />
              −2σ lower bound
            </span>
          </div>
        </div>
      )}
      {/* {!loading && analysisData && !surface3d && (
        <p
          style={{
            marginTop: "24px",
            color: "#aaa",
            fontSize: "12px",
            fontStyle: "italic",
          }}
        >
          3D analysis unavailable — panel rotation data not found for this site.
          Set <code>PANEL_ROTATION_PATH</code> in server/.env to enable.
        </p>
      )} */}
    </div>
  );
}
